/**
 * Response key normalisation.
 *
 * The DHA Partner Integration API Field Specification v1.4.1 documents every
 * response in PascalCase. The gateway does not use it. Confirmed against the dev
 * host on 2026-08-20 across seven endpoints:
 *
 *   §4.1  eligibility          {"eligible", "ineligibleReason", "availableProducts"}
 *   §4.13 open applications    {"applications"}
 *   §4.16 branches             {"branches": [{"code", "name"}]}
 *   §4.17 declaration-wording  {"declarations": [{"declarationNo", …}]}
 *   §4.18 document-costs       {"documents": [{"productCategory", …}]}
 *   §4.19 couriers             {"couriers": [{"code", "name"}]}
 *   §5.2.1 bio-enrolment apps  {"appRefNos"}
 *
 * Seven for seven, so the gateway is taken to be uniformly lowerCamel. The other
 * eight endpoints answer 403 until DHA provisions a test identity (On-boarding
 * Guide §8.6), and cannot be checked.
 *
 * Rather than guess per endpoint, every response passes through here on its way to
 * zod. DTOs are then written in camelCase alone and parse correctly whichever
 * casing an unverified endpoint turns out to use. When the spec and the wire
 * disagree, this is the one place that has to know.
 *
 * What this deliberately does NOT do:
 *
 *   - Touch values. Base64 and free text must survive byte-identical, so only keys
 *     are rewritten.
 *   - Fix the spec's internal spellings. `DateofBirth` becomes `dateofBirth`, which
 *     is still not `dateOfBirth` — a lowercase 'o' mid-word is a different problem,
 *     handled per-field in the DTO that suffers from it.
 *   - Run on error bodies. The gateway's error envelope has its own reader in
 *     errors.ts, and `validation_failures` is keyed by field name — data, not a
 *     schema — which must not be rewritten.
 */

/** `AppRefNo` -> `appRefNo`. Already-camel keys are returned unchanged. */
function lowerFirst(key: string): string {
  if (key.length === 0) return key
  const first = key[0] as string
  const lowered = first.toLowerCase()
  return lowered === first ? key : `${lowered}${key.slice(1)}`
}

/**
 * Recursively lower-cases the first character of every object key.
 *
 * Idempotent: normalising an already-normalised value is a no-op, which matters
 * because the mock backend already speaks camelCase and its responses pass through
 * here too.
 */
export function normaliseResponseKeys<T>(value: T): T {
  return transform(value) as T
}

function transform(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(transform)
  }

  // Strings, numbers, booleans, null and undefined are values, not keys.
  if (value === null || typeof value !== 'object') {
    return value
  }

  // A Date cannot appear in a parsed JSON response, but `request()` is generic and
  // a caller could hand one to a mock. Cloning it into a plain object would be a
  // silent data loss.
  if (value instanceof Date) return value

  const result: Record<string, unknown> = {}
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    result[lowerFirst(key)] = transform(entry)
  }
  return result
}
